import React from 'react';
import { useResusContext } from './ResusContext';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDroplet } from '@fortawesome/free-solid-svg-icons';
import {
  calcDilutionPerKg,
  calcInfusionSpeed,
  getTargetVolumePerHour,
  dripUsesMaxDose
} from './medicationUtils';
import { getMedicationDetails } from './predicateUtils';
import { Drip, PredicateEvalResult } from "../Cpr/types";
import './DrugsAndDrips.scss';
import { isHebrewOrArabicText } from './languageUtils';

interface DripProps {
  drip: Drip;
  customComment?: string;
  predicateResult?: PredicateEvalResult | null;
}

const DripComponent: React.FC<DripProps> = ({ drip, customComment, predicateResult}) => {
  const { age, weight } = useResusContext();
  const { t } = useTranslation(['protocols']);

  const dripDetails = getMedicationDetails(drip, age || 0, weight || 0, t) as Drip;
  const getCommentStyle = (text: string): React.CSSProperties => {
    return (isHebrewOrArabicText(text) ? { direction: 'rtl', textAlign: 'right' } : {}) as React.CSSProperties;
  };

  // If predicate exists but doesn't pass, show grayed out version with message
  if (predicateResult && !predicateResult.isValid) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        textAlign: 'left',
        color: 'var(--page-font-color)',
        opacity: 0.5        
      }}>
        <span>
          <FontAwesomeIcon icon={faDroplet} className="me-2" />
          {drip.name}
          <br />
          <span style={{ color: '#ff0000' }}>{predicateResult.message}</span>
        </span>
      </div>
    );
  }

  return (
    <div id={`drug-${drip.id}`} style={{ width: '100%', color: 'var(--page-font-color)'}}>
      <div style={{ display: 'flex', justifyContent: 'space-between', textAlign: 'left', color: 'var(--page-font-color)' }}>
        <span>
          {dripDetails.calc_type === 'DilutionInstructions' ? (
            <>
              <FontAwesomeIcon icon={faDroplet} className="me-2" />
              {dripDetails.name}:
              <b> {calcDilutionPerKg(dripDetails, weight).doseForDilution} {calcDilutionPerKg(dripDetails, weight).unitsForDilution} </b>
              in
              <b> {dripDetails.default_dilution_volume_ml} ml </b>
            </>
          ) : (
            <>
              <FontAwesomeIcon icon={faDroplet} className="me-2" />
              {dripDetails.name}:
              Use concentration of {dripDetails.existing_dilution_concentration} 
              {' ' + dripDetails.existing_dilution_concentration_dose_unit}/ml
            </>
          )}
          
          <br />
          Drip rate: <b>{dripDetails.calc_type ==='DilutionInstructions' ? 
          getTargetVolumePerHour(dripDetails) + ' ml/Hr' : 
          calcInfusionSpeed(dripDetails, weight) + ' ml/Hr'  }</b>    

          {dripUsesMaxDose(dripDetails, weight) && (
            <>
              <br />
              <span style={{ color: 'red', fontSize: '0.9rem' }}>
                Used max dose of {dripDetails.max_dose_per_hour} {dripDetails.dose_unit} per hour
              </span>
            </>
          )}

        </span>        
      </div>
          {dripDetails.comment && (
          <div className="drug-comment" style={getCommentStyle(dripDetails.comment)}>
            {dripDetails.comment}
          </div>
          )} 
          {customComment && (
            <>
              <div className="drug-comment" style={getCommentStyle(t(`protocols:${customComment}`))}>
                {t(`protocols:${customComment}`)}
              </div>
            </>          
          )}
    </div>
  );
};

export default DripComponent;